'use client';

import { Loader2, Maximize2, Minimize2, Sparkles, Volume2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OutreachAction } from '@/lib/ai/agents/outreach-skills';
import type { OutreachTone } from '@/lib/outreach/types';

type OutreachQuickActionsProps = {
  disabled?: boolean;
  loading?: boolean;
  onAction: (action: OutreachAction, tone?: OutreachTone, redraft?: boolean) => void;
};

export function OutreachQuickActions({
  disabled,
  loading,
  onAction,
}: OutreachQuickActionsProps) {
  return (
    <div className="space-y-3 rounded-lg border bg-slate-50/80 p-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Quick refine
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || loading}
          onClick={() => onAction('improve')}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <Wand2 className="h-3.5 w-3.5 mr-1" />
          )}
          Improve
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || loading}
          onClick={() => onAction('shorten')}
        >
          <Minimize2 className="h-3.5 w-3.5 mr-1" />
          Shorter
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || loading}
          onClick={() => onAction('expand')}
        >
          <Maximize2 className="h-3.5 w-3.5 mr-1" />
          Add detail
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Volume2 className="h-3.5 w-3.5" />
          Tone
        </span>
        {(['professional', 'friendly', 'concise'] as const).map((toneOption) => (
          <Button
            key={toneOption}
            type="button"
            variant="outline"
            size="sm"
            className="h-8 capitalize"
            disabled={disabled || loading}
            onClick={() => onAction('tone', toneOption)}
          >
            {toneOption}
          </Button>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 ml-auto"
          disabled={disabled || loading}
          onClick={() => onAction('draft', undefined, true)}
        >
          <Sparkles className="h-3.5 w-3.5 mr-1" />
          Redraft
        </Button>
      </div>
    </div>
  );
}
